import {
  Clock,
  DocumentText,
  Pause,
  PencilSquare,
  PlaySolid,
  Plus,
  XCircle,
} from '@medusajs/icons';
import {
  Button,
  Checkbox,
  Combobox,
  Drawer,
  Input,
  Label,
  Select,
  Skeleton,
  StatusBadge,
  Table,
  Text,
  Textarea,
  toast,
} from '@pcrm/fe/ui-kit';
import { CURRENCIES, currencyCodes, currencyIsoCode } from '@pcrm/shared/money';
import { MIN_MONTHS, today } from '@pcrm/shared/period';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ActionMenu } from '../../components/action-menu';
import { Field } from '../../components/field';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { fromCustomer } from '../documents/document-origin';
import { documentStateDescriptor } from '../documents/documents.api';
import { PackageTypeIcon } from '../settings/package-type-icons';
import { packagesQueryOptions } from '../settings/packages.api';
import {
  ALL_TYPES,
  applyPackage,
  applyTypeFilter,
  empty,
  expireDateChange,
  packagesOfType,
  packageTypes,
  plannedExpireDate,
  toInput,
} from './subscription-form-rules';
import {
  changeSubscriptionStatus,
  createSubscription,
  isEditable,
  nextStatuses,
  SUBSCRIPTION_STATUS,
  type Subscription,
  type SubscriptionHistoryItem,
  type SubscriptionInput,
  type SubscriptionListItem,
  type SubscriptionStatus,
  subscriptionHistoryQueryKey,
  subscriptionHistoryQueryOptions,
  subscriptionStatusDescriptor,
  subscriptionsQueryKey,
  subscriptionsQueryOptions,
  updateSubscription,
} from './subscriptions.api';

/**
 * Abonamentele clientului — **un client, un pachet, o adresă, un preț**.
 *
 * Un client care cumpără internet + telefonie + colocare are trei rânduri aici, deci trei
 * anexe. Adresa de instalare stă pe abonament, ca un rând: e ce se tipărește pe anexă.
 * ⚠️ Consecința asumată: la același sediu, adresa se scrie de trei ori.
 *
 * **Abonamentul se naște în „în așteptare semnare", nu activ**, și devine activ când anexa
 * lui e semnată. Cât e acolo, se editează liber; după activare, orice schimbare comercială
 * cere o anexă nouă (E4, #15) — de asta „Modifică" dispare din meniu.
 *
 * **Fără ștergere.** Un abonament are istoric de stări în urma lui; se reziliază.
 * Vezi docs/wiki/domains/subscriptions.md
 */
export function CustomerSubscriptions({
  customerId,
  archived,
}: {
  /** `null` cât timp fișa nu e salvată: un abonament are nevoie de un client care există. */
  customerId: string | null;
  /** Clientul e arhivat: nu i se mai adaugă servicii. Vezi `assertNotArchived` pe server. */
  archived: boolean;
}) {
  const query = useQuery({
    ...subscriptionsQueryOptions(customerId ?? ''),
    enabled: customerId !== null,
  });

  const [editing, setEditing] = useState<SubscriptionListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [transitioning, setTransitioning] = useState<{
    subscription: SubscriptionListItem;
    to: SubscriptionStatus;
  } | null>(null);
  const [historyOf, setHistoryOf] = useState<SubscriptionListItem | null>(null);

  return (
    <>
      <SectionContainer>
        <SectionHeader
          title="Abonamente"
          actions={
            // Ca la documente: pe un client arhivat butonul dispare și se spune de ce.
            // Arhivarea tocmai a reziliat tot ce avea (#111); un serviciu nou peste ea ar fi
            // un abonament activ pe un client inactiv. Serverul refuză oricum.
            !customerId ? null : archived ? (
              <Text size="small" className="text-ui-fg-muted">
                Client arhivat — nu i se mai adaugă servicii
              </Text>
            ) : (
              <Button size="small" variant="secondary" onClick={() => setCreating(true)}>
                <Plus />
                Adaugă
              </Button>
            )
          }
        />

        {!customerId ? (
          // Onest, nu ascuns: butonul lipsește pentru că abonamentul are nevoie de un
          // client salvat și de o locație a lui.
          <SectionEmpty>Salvează întâi fișa clientului, apoi adaugă abonamentele.</SectionEmpty>
        ) : query.isPending ? (
          <div className="flex flex-col gap-3 px-6 py-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : query.error ? (
          <SectionEmpty tone="error">
            {query.error instanceof Error
              ? query.error.message
              : 'Abonamentele nu au putut fi citite.'}
          </SectionEmpty>
        ) : query.data.length === 0 ? (
          <SectionEmpty>Niciun abonament.</SectionEmpty>
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Abonament</Table.HeaderCell>
                <Table.HeaderCell className="w-[200px]">Pachet</Table.HeaderCell>
                <Table.HeaderCell className="w-[220px]">Adresă</Table.HeaderCell>
                <Table.HeaderCell className="w-[140px]">Preț</Table.HeaderCell>
                <Table.HeaderCell className="w-[180px]">Stare</Table.HeaderCell>
                <Table.HeaderCell className="w-[60px]" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {query.data.map((subscription) => (
                <SubscriptionRow
                  key={subscription.id}
                  subscription={subscription}
                  onEdit={() => setEditing(subscription)}
                  onHistory={() => setHistoryOf(subscription)}
                  onTransition={(to) => setTransitioning({ subscription, to })}
                />
              ))}
            </Table.Body>
          </Table>
        )}
      </SectionContainer>

      {customerId ? (
        <SubscriptionDrawer
          customerId={customerId}
          subscription={editing}
          open={creating || editing !== null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      ) : null}

      <TransitionDrawer
        request={transitioning}
        onClose={() => setTransitioning(null)}
        customerId={customerId}
      />

      <HistoryDrawer subscription={historyOf} onClose={() => setHistoryOf(null)} />
    </>
  );
}

/**
 * Iconul acțiunii, rezolvat din descriptor.
 *
 * Descriptorul poartă un **nume**, nu componenta: `subscriptions.api.ts` e stratul de
 * date, iar un import de JSX acolo l-ar lega de setul de iconuri.
 */
const TRANSITION_ICONS = {
  clock: <Clock />,
  play: <PlaySolid />,
  pause: <Pause />,
  x: <XCircle />,
} as const;

function SubscriptionRow({
  subscription,
  onEdit,
  onHistory,
  onTransition,
}: {
  subscription: SubscriptionListItem;
  onEdit: () => void;
  onHistory: () => void;
  onTransition: (to: SubscriptionStatus) => void;
}) {
  const status = subscriptionStatusDescriptor(subscription.status);
  const transitions = nextStatuses(subscription.status);
  const navigate = useNavigate();
  // Legat într-un `const`, nu citit din `subscription` în `onClick`: TypeScript nu păstrează
  // îngustarea unei proprietăți peste granița unei funcții, iar altfel ar fi cerut un `?? ''`
  // — adică o rută inventată exact în cazul pe care ramura de mai jos îl exclude.
  const documentId = subscription.documentId;

  return (
    <Table.Row>
      <Table.Cell>
        <div className="flex flex-col">
          <span>{subscription.name}</span>
          {/* Perioada sub denumire, nu în coloană proprie: e goală la majoritatea
              abonamentelor, iar o coloană de liniuțe ocupă lățime fără să spună nimic. */}
          {subscription.startDate || subscription.expireDate ? (
            <Text size="small" className="text-ui-fg-subtle">
              {subscription.startDate ? formatDate(subscription.startDate) : '…'} –{' '}
              {subscription.expireDate ? formatDate(subscription.expireDate) : '…'}
            </Text>
          ) : null}
        </div>
      </Table.Cell>
      <Table.Cell className="text-ui-fg-subtle">
        {/*
          Iconul e al **tipului de serviciu**, deci stă pe rândul tipului, nu pe al denumirii:
          „Internet 300" și „Internet 1 Gbps" sunt două pachete cu același simbol, iar pus
          lângă denumire ar părea că le deosebește.
        */}
        <div className="flex flex-col">
          <span>{subscription.packageName}</span>
          <Text size="small" className="flex items-center gap-x-1.5 text-ui-fg-muted">
            <PackageTypeIcon icon={subscription.packageTypeIcon} className="size-3.5 shrink-0" />
            {subscription.packageTypeName}
          </Text>
        </div>
      </Table.Cell>
      <Table.Cell className="text-ui-fg-subtle">{subscription.address ?? '-'}</Table.Cell>
      {/*
        Aliniat la dreapta: sumele se compară pe verticală, cifră cu cifră. „cu TVA" /
        „fără TVA" stă **lângă sumă**, nu în coloană proprie: e ce înseamnă numărul, iar
        despărțite s-ar putea citi două rânduri și confunda convenția între ele.
      */}
      <Table.Cell className="text-right tabular-nums">
        {/*
          „Fără preț" scris ca atare, nu `0,00 RON` și nici o liniuță: serviciul chiar n-are
          preț, iar zeroul ar fi o sumă inventată — aceeași sumă care ajungea pe anexă.
        */}
        {subscription.cost === null ? (
          <span className="text-ui-fg-muted">Fără preț</span>
        ) : (
          <>
            {formatMoney(subscription.cost, subscription.currency)}
            <span className="text-ui-fg-muted ms-1">
              {subscription.priceIncludesVat ? 'cu TVA' : 'fără TVA'}
            </span>
          </>
        )}
      </Table.Cell>
      <Table.Cell>
        <div className="flex flex-col items-start gap-y-1">
          <StatusBadge color={status.color}>{status.label}</StatusBadge>
          {/*
            Până când ține suspendarea, sub bulină: „Suspendat" singur nu spune nimic despre
            termen, iar altfel operatorul ar trebui să deschidă istoricul ca să afle.
            ⚠️ Data e informativă — nimic nu se reactivează singur.
          */}
          {subscription.resumesAt ? (
            <Text size="small" className="text-ui-fg-subtle">
              până la {formatDate(subscription.resumesAt)}
            </Text>
          ) : null}
        </div>
      </Table.Cell>
      <Table.Cell>
        <ActionMenu
          groups={[
            {
              actions: [
                // „Modifică" **lipsește** după activare, nu apare dezactivat: butonul gri
                // ar sugera că e o chestiune de drepturi, când e o regulă de contract.
                ...(isEditable(subscription.status)
                  ? [{ icon: <PencilSquare />, label: 'Modifică', onClick: onEdit }]
                  : []),
                // **Al doilea clic al Fazei 1**: de la abonament la actul care îl justifică,
                // iar de acolo PDF-ul semnat se deschide cu „Vezi PDF".
                //
                // ⚠️ **Lipsește când nu există act**, nu apare stinsă — aceeași regulă ca
                // „Modifică" după activare: un rând gri s-ar citi ca lipsă de drepturi, când
                // de fapt abonamentul încă n-a fost semnat (sau ciorna lui a fost ștearsă).
                ...(documentId === null
                  ? []
                  : [
                      {
                        icon: <DocumentText />,
                        label: 'Vezi actul',
                        // „Înapoi" din document duce înapoi aici, pe Servicii (#222).
                        onClick: () =>
                          void navigate({
                            to: '/documente/$documentId',
                            params: { documentId },
                            search: fromCustomer(subscription.customerId, 'servicii'),
                          }),
                      },
                    ]),
                { icon: <Clock />, label: 'Istoric stări', onClick: onHistory },
              ],
            },
            {
              actions: transitions.map((to) => {
                const target = subscriptionStatusDescriptor(to);
                return {
                  icon: TRANSITION_ICONS[target.actionIcon],
                  label: target.actionLabel,
                  onClick: () => onTransition(to),
                };
              }),
            },
          ]}
        />
      </Table.Cell>
    </Table.Row>
  );
}

/**
 * Schimbarea de stare, cu motivul ei.
 *
 * Sertar și nu un `Prompt`: suspendarea și rezilierea **cer** un motiv, iar dialogul de
 * confirmare n-are unde să-l primească. Motivul e ce se citește peste șase luni din
 * istoric; cerut într-un câmp separat, nu ajunge o notă pierdută pe fișă.
 */
function TransitionDrawer({
  request,
  customerId,
  onClose,
}: {
  request: { subscription: SubscriptionListItem; to: SubscriptionStatus } | null;
  customerId: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  /** Ziua până la care e suspendat serviciul. Goală la deschidere: n-o presupune nimeni. */
  const [resumesAt, setResumesAt] = useState('');

  useEffect(() => {
    if (request) {
      setReason('');
      setResumesAt('');
    }
  }, [request]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!request) {
        throw new Error('Nicio tranziție de aplicat.');
      }
      return changeSubscriptionStatus(request.subscription.id, request.to, reason, resumesAt);
    },
    onSuccess: async () => {
      toast.success('Starea abonamentului a fost schimbată');
      if (customerId) {
        await queryClient.invalidateQueries({ queryKey: subscriptionsQueryKey(customerId) });
      }
      if (request) {
        // Și istoricul: e tocmai rândul pe care tranziția l-a scris, iar sertarul de
        // istoric se poate deschide imediat după.
        await queryClient.invalidateQueries({
          queryKey: subscriptionHistoryQueryKey(request.subscription.id),
        });
      }
      onClose();
    },
    onError: (error: unknown) => {
      // Mesajul serverului: „Abonamentul nu poate trece din starea 1 în starea 1" spune
      // că altcineva a schimbat starea între încărcarea paginii și apăsare.
      toast.error(error instanceof ApiError ? error.message : 'Tranziția nu a putut fi aplicată.');
    },
  });

  const descriptor = request ? subscriptionStatusDescriptor(request.to) : null;
  const needsReason = descriptor?.needsReason ?? false;
  const needsResumeDate = descriptor?.needsResumeDate ?? false;

  return (
    <Drawer open={request !== null} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>
            {descriptor ? `${descriptor.actionLabel} „${request?.subscription.name}"` : ''}
          </Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4">
          <Field label="Motiv" id="tranzitie-motiv" required={needsReason}>
            <Textarea
              id="tranzitie-motiv"
              rows={3}
              maxLength={255}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              aria-required={needsReason || undefined}
              disabled={mutation.isPending}
            />
          </Field>

          {/*
            **Ziua aleasă, nu un număr de luni** (fondatorul, 2026-08-19): „cum ar trebui să
            interpretez N luni" a fost exact întrebarea, iar răspunsul e că nu se
            interpretează. ⚠️ Data e informativă — nimic nu se reactivează singur la ea.

            `min` pe ziua de azi: rândul de istoric e append-only, deci o dată tastată greșit
            în trecut n-ar mai putea fi corectată. Serverul o refuză oricum.
          */}
          {needsResumeDate ? (
            <Field label="Suspendat până la" id="tranzitie-reactivare" required>
              <Input
                id="tranzitie-reactivare"
                type="date"
                min={today()}
                value={resumesAt}
                onChange={(event) => setResumesAt(event.target.value)}
                aria-required
                disabled={mutation.isPending}
              />
            </Field>
          ) : null}
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Renunță
          </Button>
          <Button
            variant={request?.to === SUBSCRIPTION_STATUS.terminated ? 'danger' : 'primary'}
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            disabled={
              mutation.isPending ||
              (needsReason && reason.trim().length === 0) ||
              (needsResumeDate && resumesAt.length === 0)
            }
          >
            {descriptor?.actionLabel ?? 'Confirmă'}
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}

/** Istoricul de stări: cronologic, de la naștere. Se citește, nu se corectează. */
function HistoryDrawer({
  subscription,
  onClose,
}: {
  subscription: SubscriptionListItem | null;
  onClose: () => void;
}) {
  const query = useQuery(subscriptionHistoryQueryOptions(subscription?.id ?? null));

  return (
    <Drawer open={subscription !== null} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Istoric — {subscription?.name ?? ''}</Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="overflow-y-auto">
          {query.isPending ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          ) : query.error ? (
            <Text size="small" className="text-ui-fg-error">
              {query.error instanceof Error
                ? query.error.message
                : 'Istoricul nu a putut fi citit.'}
            </Text>
          ) : query.data.length === 0 ? (
            <Text size="small" className="text-ui-fg-subtle">
              Nicio tranziție înregistrată.
            </Text>
          ) : (
            <ol className="flex flex-col gap-y-4">
              {query.data.map((entry) => {
                const status = subscriptionStatusDescriptor(entry.status);
                return (
                  <li key={entry.id} className="flex flex-col gap-y-1 border-l-2 ps-4">
                    <div className="flex items-center gap-x-2">
                      <StatusBadge color={status.color}>{status.label}</StatusBadge>
                      <Text size="small" className="text-ui-fg-subtle">
                        {formatDateTime(entry.changedAt)}
                      </Text>
                    </div>
                    <Text size="small" className="text-ui-fg-subtle">
                      {/* `null` = tranziție de sistem (seed, migrare), nu operator
                          necunoscut — la fel ca în jurnalul de activitate. */}
                      {entry.changedByName ?? 'Sistem'}
                    </Text>
                    {entry.reason ? (
                      <Text size="small" className="whitespace-pre-line text-pretty">
                        {entry.reason}
                      </Text>
                    ) : null}
                    {/* Lângă tranziția care a produs-o: e durata *acelei* suspendări. */}
                    {entry.resumesAt ? (
                      <Text size="small" className="text-ui-fg-subtle">
                        până la {formatDate(entry.resumesAt)}
                      </Text>
                    ) : null}
                    <HistoryDocument entry={entry} customerId={subscription?.customerId} />
                  </li>
                );
              })}
            </ol>
          )}
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose}>
            Închide
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}

/**
 * **Actul care a produs tranziția**, pe rândul ei din istoric.
 *
 * Răspunde la „pe ce bază a intrat abonamentul în starea asta" chiar acolo unde se pune
 * întrebarea. Rândurile fără act — suspendarea și rezilierea făcute din interfață — nu
 * capătă unul inventat: ele au motiv și autor, scrise deasupra.
 *
 * ⚠️ **Un act anulat, reziliat sau arhivat rămâne proba perioadei lui.** Linkul nu dispare;
 * insigna de lângă număr spune ce s-a întâmplat cu actul de atunci. Ascuns, ar fi șters
 * exact răspunsul pentru care rândul ăsta există.
 */
function HistoryDocument({
  entry,
  customerId,
}: {
  entry: SubscriptionHistoryItem;
  /** Fișa din care s-a deschis istoricul — acolo se întoarce „Înapoi" din document (#222). */
  customerId: string | undefined;
}) {
  if (entry.documentId === null) {
    return null;
  }

  // ⚠️ **Starea lipsește doar pe un rând fără act.** `fk_hist_doc` garantează că un
  // `document_id` scris duce la un rând real, iar `document_state` e `NOT NULL` — deci
  // ramura de `null` e o consecință a tipului, în care cele trei câmpuri sunt nule
  // independent, nu un caz care poate veni din bază.
  const state = entry.documentState === null ? null : documentStateDescriptor(entry.documentState);

  return (
    <div className="flex items-center gap-x-2">
      <Link
        to="/documente/$documentId"
        params={{ documentId: entry.documentId }}
        search={customerId === undefined ? {} : fromCustomer(customerId, 'servicii')}
        className="text-ui-fg-interactive text-xs hover:underline"
      >
        {/*
          ⚠️ **„Fără număr", nu gol.** O ciornă n-are număr (#142), iar un link cu eticheta
          goală pare stricat — exact citirea greșită evitată și în registru, unde celula
          scrie „—".
        */}
        {entry.documentNumber === null ? 'Act fără număr' : `Actul nr. ${entry.documentNumber}`}
      </Link>
      {state ? <StatusBadge color={state.color}>{state.label}</StatusBadge> : null}
    </div>
  );
}

/** Același sertar pentru adăugare și pentru editare — diferă doar titlul și ce trimite. */
function SubscriptionDrawer({
  customerId,
  subscription,
  open,
  onClose,
}: {
  customerId: string;
  subscription: SubscriptionListItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // Numai pachetele active: catalogul dezactivat nu se mai vinde. Pachetul abonamentului
  // editat se adaugă înapoi mai jos, ca să nu dispară din propriul lui formular.
  const packages = useQuery(packagesQueryOptions(false));
  const [form, setForm] = useState<SubscriptionInput>(empty());
  const [errors, setErrors] = useState<Partial<Record<keyof SubscriptionInput, string>>>({});
  /** Ce a completat catalogul singur. Vezi `choosePackage`. */
  const [prefilled, setPrefilled] = useState(true);
  /**
   * Filtrul de tip, cât timp l-a atins operatorul. `null` = nu l-a atins, iar atunci tipul
   * se citește din pachetul salvat — la modificare, lista pornește restrânsă la tipul lui.
   * Ținut ca stare separată, nu dedus mereu din pachet: „Toate tipurile" trebuie să rămână
   * o alegere valabilă chiar și cu un pachet ales.
   */
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(subscription ? toInput(subscription) : empty());
      setErrors({});
      setPrefilled(subscription === null);
      setTypeFilter(null);
    }
  }, [open, subscription]);

  const set = <TField extends keyof SubscriptionInput>(
    field: TField,
    value: SubscriptionInput[TField],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
  };

  /**
   * Alegerea pachetului prepopulează denumirea, prețul și moneda.
   *
   * Numai cât operatorul n-a scris el ceva: prepopularea e o comoditate, iar suprascrierea
   * unui preț negociat, tastat deja, ar fi o pierdere tăcută de date. De asta `prefilled`
   * ține minte dacă valorile de acum sunt ale catalogului sau ale lui.
   */
  const choosePackage = (packageId: string) => {
    const chosen = (packages.data ?? []).find((item) => item.id === packageId);
    if (!chosen) {
      return;
    }

    setForm((current) => applyPackage(current, chosen, prefilled));
    setErrors((current) => (current.packageId ? { ...current, packageId: undefined } : current));
  };

  /**
   * Filtrul de tip **nu e o schimbare de abonament.**
   *
   * Golește pachetul care a ieșit din listă și nimic altceva: `prefilled` rămâne cum era,
   * altfel următorul pachet ales ar readuce prețul de listă peste unul negociat, tastat.
   * Vezi `applyTypeFilter`.
   */
  const chooseTypeFilter = (typeId: string) => {
    setTypeFilter(typeId);
    setForm((current) => applyTypeFilter(current, packages.data ?? [], typeId));
  };

  const mutation = useMutation({
    mutationFn: () =>
      subscription
        ? updateSubscription(subscription.id, form)
        : createSubscription(customerId, form),
    onSuccess: async (saved: Subscription) => {
      toast.success(subscription ? 'Abonament actualizat' : 'Abonament adăugat');
      await queryClient.invalidateQueries({ queryKey: subscriptionsQueryKey(saved.customerId) });
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.issues.length > 0) {
        const fields: Partial<Record<keyof SubscriptionInput, string>> = {};
        for (const issue of error.issues) {
          if (issue.path in empty()) {
            fields[issue.path as keyof SubscriptionInput] = issue.message;
          }
        }
        setErrors(fields);
        return;
      }

      toast.error(error instanceof ApiError ? error.message : 'Salvarea a eșuat.');
    },
  });

  const availablePackages = (packages.data ?? []).filter(
    (item) => item.status || item.id === form.packageId,
  );

  const chosenPackage = availablePackages.find((item) => item.id === form.packageId);
  // Cât operatorul n-a atins filtrul, tipul e al pachetului salvat: la modificare, lista
  // pornește restrânsă la el. Derivat, nu pus într-un `useEffect`: pachetele se încarcă
  // asincron, iar un efect ar trebui să aștepte după ele.
  const activeType = typeFilter ?? chosenPackage?.typeId ?? ALL_TYPES;
  const packageOptions = packagesOfType(availablePackages, activeType).map((item) => ({
    value: item.id,
    label: item.name,
    description: item.typeName,
    icon: <PackageTypeIcon icon={item.typeIcon} />,
  }));
  const listPrice = chosenPackage?.costDefault ?? null;
  // Comparație de string-uri, ambele normalizate de server: prețul nu trece prin `number`
  // nici măcar ca să se compare cu lista. Convenția de TVA intră în comparație — 49,99 cu
  // TVA și 49,99 fără TVA **nu** sunt același preț, iar „la fel ca lista" ar fi o minciună.
  const differsFromList =
    listPrice !== null &&
    form.cost !== null &&
    (listPrice !== form.cost || chosenPackage?.priceIncludesVat !== form.priceIncludesVat);

  /** Abonamentul e declarat fără preț. Starea din bază e chiar `cost IS NULL`. */
  const unpriced = form.cost === null;

  // Aceeași funcție ca pe server (`@pcrm/shared/period`), nu o a doua implementare: data
  // arătată aici e chiar cea care se scrie în coloană și se tipărește pe anexă.
  const plannedExpire = plannedExpireDate(form);
  const expireChange = expireDateChange(form, subscription);

  const canSave = form.packageId.length > 0 && form.name.trim().length > 0;

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>
            {subscription ? `Modifică „${subscription.name}"` : 'Adaugă un abonament'}
          </Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          {/*
            Tipul **înaintea** pachetului: restrânge lista, nu o etichetează. „Toate tipurile"
            rămâne o alegere validă — cine știe denumirea n-are de ce să treacă întâi prin tip.
            Tipurile vin din pachetele încărcate, nu dintr-o cerere separată: un tip fără
            niciun pachet de ales ar duce la o listă goală.
          */}
          <Field label="Tip de pachet" id="abonament-tip">
            <Select
              value={activeType}
              onValueChange={chooseTypeFilter}
              disabled={mutation.isPending}
            >
              <Select.Trigger id="abonament-tip">
                <Select.Value />
              </Select.Trigger>
              <Select.Content className="z-50">
                <Select.Item value={ALL_TYPES}>Toate tipurile</Select.Item>
                {packageTypes(availablePackages).map((type) => (
                  <Select.Item key={type.id} value={type.id}>
                    <span className="flex items-center gap-x-2">
                      <PackageTypeIcon icon={type.icon} />
                      {type.name}
                    </span>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </Field>

          <Field label="Pachet" id="abonament-pachet" required error={errors.packageId}>
            {/* Listă închisă, cu căutare: se alege un pachet existent, nu se scrie unul nou. */}
            <Combobox
              id="abonament-pachet"
              value={form.packageId}
              onChange={choosePackage}
              options={packageOptions}
              placeholder="Caută după denumire sau tip…"
              emptyText="Niciun pachet pe filtrul ăsta."
              required
              invalid={errors.packageId !== undefined}
              disabled={mutation.isPending}
            />
          </Field>

          <Field label="Denumire" id="abonament-nume" required error={errors.name}>
            <Input
              id="abonament-nume"
              value={form.name}
              onChange={(event) => {
                setPrefilled(false);
                set('name', event.target.value);
              }}
              maxLength={255}
              aria-required
              aria-invalid={errors.name ? true : undefined}
              disabled={mutation.isPending}
            />
          </Field>

          <div className="grid grid-cols-[1fr_140px] gap-4">
            <Field
              label="Preț negociat"
              id="abonament-pret"
              error={errors.cost}
              // Diferența față de listă rămâne: e o informație, nu o explicație — altfel
              // discountul se vede abia dintr-un raport, peste trei luni.
              hint={
                differsFromList && listPrice !== null ? (
                  <>
                    Listă:{' '}
                    <span className="text-ui-fg-base">
                      {formatMoney(listPrice, chosenPackage?.currency ?? CURRENCIES.ron)}{' '}
                      {chosenPackage?.priceIncludesVat ? 'cu TVA' : 'fără TVA'}
                    </span>
                  </>
                ) : undefined
              }
            >
              {/*
                `type="text"`, nu `type="number"`: rotița mouse-ului ar schimba prețul peste
                care trece cursorul, iar tastatura românească dă virgulă, pe care controlul
                numeric o refuză tăcut. Suma pleacă spre server ca text.
              */}
              <Input
                id="abonament-pret"
                inputMode="decimal"
                value={form.cost ?? ''}
                onChange={(event) => {
                  setPrefilled(false);
                  set('cost', event.target.value);
                }}
                className="text-right tabular-nums"
                disabled={unpriced || mutation.isPending}
              />
            </Field>

            <Field label="Monedă" id="abonament-moneda" error={errors.currency}>
              <Select
                value={String(form.currency)}
                onValueChange={(value) => {
                  setPrefilled(false);
                  set('currency', Number(value) as SubscriptionInput['currency']);
                }}
                disabled={unpriced || mutation.isPending}
              >
                <Select.Trigger id="abonament-moneda">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content className="z-50">
                  {currencyCodes.map((code) => (
                    <Select.Item key={code} value={String(code)}>
                      {currencyIsoCode(code)}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            </Field>
          </div>

          {/*
            **Bifă explicită, nu câmp gol.** Un preț uitat trebuie să rămână o eroare de
            validare: dacă „am uitat" și „n-are preț" ar produce același rând, un abonament ar
            deveni tăcut netarifat, iar greșeala s-ar vedea peste luni, în bani.
          */}
          <div className="flex items-center gap-x-3">
            <Checkbox
              id="abonament-fara-pret"
              checked={unpriced}
              onCheckedChange={(checked) => {
                setPrefilled(false);
                // La debifare câmpul rămâne gol, nu revine la zero: zero e o sumă, iar
                // operatorul trebuie să scrie prețul, nu să-l confirme.
                set('cost', checked === true ? null : '');
              }}
              disabled={mutation.isPending}
            />
            <Label htmlFor="abonament-fara-pret">Fără preț</Label>
          </div>

          {/* Convenția de TVA descrie o sumă. Fără sumă, n-are ce descrie. */}
          {unpriced ? null : (
            <Field label="Ce reprezintă prețul" id="abonament-tva" error={errors.priceIncludesVat}>
              <Select
                value={form.priceIncludesVat ? 'cu' : 'fara'}
                onValueChange={(value) => {
                  setPrefilled(false);
                  set('priceIncludesVat', value === 'cu');
                }}
                disabled={mutation.isPending}
              >
                <Select.Trigger id="abonament-tva">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content className="z-50">
                  <Select.Item value="fara">Preț fără TVA</Select.Item>
                  <Select.Item value="cu">Preț cu TVA inclus</Select.Item>
                </Select.Content>
              </Select>
            </Field>
          )}

          {/*
            `Textarea`, nu `Input`: adresa completă nu încape într-un rând de control, iar
            operatorul trebuie s-o vadă întreagă înainte s-o semneze clientul.
          */}
          <Field label="Adresă de instalare" id="abonament-adresa" error={errors.address}>
            <Textarea
              id="abonament-adresa"
              rows={2}
              maxLength={300}
              value={form.address}
              onChange={(event) => set('address', event.target.value)}
              disabled={mutation.isPending}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Început" id="abonament-inceput" error={errors.startDate}>
              <Input
                id="abonament-inceput"
                type="date"
                value={form.startDate}
                onChange={(event) => set('startDate', event.target.value)}
                disabled={mutation.isPending}
              />
            </Field>

            {/*
              **Durata, nu data de expirare.** Data o calculează serverul, cu aceeași funcție
              (`@pcrm/shared/period`), și se scrie „ultima zi a lunii în care cade început + N
              luni". Se arată dedesubt pentru că operatorul semnează un act cu data aia pe el:
              trebuie s-o vadă înainte de a salva, nu după.
            */}
            <Field
              label="Durată (luni)"
              id="abonament-durata"
              error={errors.months}
              hint={
                plannedExpire ? (
                  <>
                    Expiră pe <span className="text-ui-fg-base">{formatDate(plannedExpire)}</span>
                  </>
                ) : undefined
              }
            >
              <Input
                id="abonament-durata"
                type="number"
                min={MIN_MONTHS}
                step={1}
                value={form.months}
                onChange={(event) => set('months', event.target.value)}
                className="text-right tabular-nums"
                aria-invalid={errors.months ? true : undefined}
                disabled={mutation.isPending}
              />
            </Field>
          </div>

          {/*
            Rândurile vechi (seed, import) pot avea o expirare care nu e ultima zi a lunii.
            Formularul arată data **stocată** și spune ce se întâmplă la salvare — nu o
            corectează tăcut, și nicio migrație nu umblă la ele: ar muta termene de pe
            contracte semnate, în masă și fără martor.
          */}
          {expireChange ? (
            <Text size="small" className="text-ui-fg-subtle" role="status">
              {expireChange.stored ? (
                <>
                  Termenul din bază e{' '}
                  <span className="text-ui-fg-base">{formatDate(expireChange.stored)}</span>.
                  Salvarea îl mută.
                </>
              ) : (
                'Abonamentul n-are termen în bază. Salvarea îi pune unul.'
              )}
            </Text>
          ) : null}
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Renunță
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            disabled={!canSave || mutation.isPending}
          >
            Salvează
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}
